<template>
  <div class="page">
    <!-- Splash de espera 5s -->
    <div v-if="showSplash" class="splash">
      <img src="@assets/images/pantalla-carga-animada.svg" alt="Cargando resultados" />
      <div class="texto-cargando">{{ $t("components.chargerresultslistview.cargando_resultados") }}</div>
    </div>

    <div v-else class="layout">
      <div class="header-container">
        <!-- 1) Cabecera -->
        <header class="header">
          <div class="title-bar">  
            <h1>{{ $t("components.chargerresultslistview.puntos_carga") }}</h1>
          </div>
          <button class="title-bar-button" :aria-label="$t('components.chargerresultslistview.filtros')" @click="openFilters"><i
              class="fa-solid fa-sliders-up"></i></button>
        </header>

        <!-- 2) Botonera de ordenación -->
        <nav class="sort-bar">
          <button v-for="option in sortOptions" :key="option.value"
            :class="['pill', { active: selectedSort === option.value }]" @click="handleSortChange(option.value)">{{
              option.label }}</button>
        </nav>
      </div>
      <!-- 3) Listado de resultados -->
      <main ref="resultsContainer" class="results" @scroll="handleScroll">
        <!-- Mensaje de carga -->
        <div v-if="isLoadingResults && results.length === 0" class="loading-message">
          <div class="loading-spinner"></div>
          <p>{{ $t("components.chargerresultslistview.buscando_estaciones") }}</p>
        </div>
        
        <!-- Mensaje de error -->
        <div v-else-if="apiError" class="error-message">
          <div class="error-icon">⚠️</div>
          <p>{{ apiError }}</p>
          <button @click="() => loadSearchResults()" class="retry-button">{{ $t("components.chargerresultslistview.reintentar") }}</button>
        </div>
        
        <!-- Sin resultados -->
        <div v-else-if="results.length === 0" class="no-results">
          <div class="no-results-icon">🔍</div>
          <p>{{ $t("components.chargerresultslistview.no_encontraron_estaciones") }}</p>
          <p class="no-results-subtitle">{{ $t("components.chargerresultslistview.ampliar_radio") }}</p>
        </div>
        
        <!-- Lista de resultados -->
        <template v-else>
          <ChargerListItem v-for="item in results" :key="item.id" :name="item.name" :distanceKm="item.distanceKm"
            :priceKwh="item.priceKwh" :totalCost="item.totalCost" :powerKw="item.powerKw"
            :chargeTimeMin="item.chargeTimeMin" :latitud="item.latitud" :longitud="item.longitud" :Libre="item.libre" :Total="item.total" />
          
          <!-- Indicador de carga de más resultados -->
          <div v-if="isLoadingMore" class="loading-more">
            <div class="loading-spinner"></div>
            <p>{{ $t("components.chargerresultslistview.cargando_mas_resultados") }}</p>
          </div>
          
          <!-- Mensaje cuando no hay más resultados -->
          <div v-else-if="!hasMoreResults && results.length > 0" class="no-more-results">
            <p>{{ $t("components.chargerresultslistview.no_mas_resultados") }}</p>
          </div>
        </template>
      </main>

      <!-- 4) Footer -->
      <ChargerResultsFooter activeTab="list" />
    </div>

    <!-- Modal de Filtros -->
    <ChargerListItemFilter v-if="showFilters" :activeFilters="activeFilters" @close="showFilters = false" @apply="handleApplyFilters" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, onActivated } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from 'vue-i18n';
import ChargerListItem from "@/components/charger/ChargerListItem.vue";
import ChargerResultsFooter from "@/components/share/ChargerResultsFooter.vue";
import ChargerListItemFilter from "@/components/charger/ChargerListItemFilter.vue";
import { useApi } from "@api/integration"

const router = useRouter();
const route = useRoute();
const api = useApi();
const { t } = useI18n();

// Referencia al contenedor de resultados para el scroll
const resultsContainer = ref<HTMLElement | null>(null);

// Datos de búsqueda desde localStorage
interface SearchData {
  vehicle: string;
  IdCocheElectrico: string;
  IdTarifaNombre: number | null;
  batteryCurrent: number;
  batteryTarget: number;
  BateriaInicial: number;
  BateriaFinal: number;
  location: { lat: number; lon: number } | null;
}

const searchData = ref<SearchData | null>(null);

// Splash 5s
const showSplash = ref(true);
const isLoadingResults = ref(false);

// Modal de filtros
const showFilters = ref(false);

// Sort options - mapeo: valor local -> OrderBy del API (según CargadorOrderByEnum: "Distancia", "Precio", "Tiempo", "Potencia")
const sortOptions = [
  { label: t("components.chargerresultslistview.mas_cercanas"), value: "distance", orderBy: "Distancia" },
  { label: t("components.chargerresultslistview.mejor_precio"), value: "price", orderBy: "Precio" },
  { label: t("components.chargerresultslistview.menor_tiempo"), value: "time", orderBy: "Tiempo" },
  { label: t("components.chargerresultslistview.mayor_potencia"), value: "power", orderBy: "Potencia" },
] as const;
const selectedSort = ref<typeof sortOptions[number]["value"]>("distance");

// Resultados reales de la API
type ChargerItem = {
  id: string;
  name: string;
  distanceKm: number;
  priceKwh: number;
  totalCost: number;
  powerKw: number;
  chargeTimeMin: number;
  latitud: number;
  longitud: number;
  libre: number;
  total: number;
};

const results = ref<ChargerItem[]>([]);
const apiError = ref<string>("");

// Variables para el scroll infinito
const isLoadingMore = ref(false);
const hasMoreResults = ref(true);
const currentSkip = ref(0);
const TAKE_SIZE = 20; // Tamaño de página

// Filtros activos
interface ActiveFilters {
  chargerStatus: string;
  priceMax: number;
  distance: number;
  chargingTime: string;
}

const activeFilters = ref<ActiveFilters | null>(null);

function formatHoraCliente(d: Date) {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// Cargar datos de búsqueda y obtener resultados
async function loadSearchResults(orderBy?: string, skip: number = 0, append: boolean = false) {
  try {
    // Leer datos de búsqueda
    const storedData = localStorage.getItem('chargerSearchData');
    if (!storedData) {
      console.warn('⚠️ No hay datos de búsqueda almacenados');
      return;
    }
    
    searchData.value = JSON.parse(storedData);
    console.log('📥 Datos de búsqueda cargados:', searchData.value);

    // Si hay ubicación, buscar cargadores cercanos
    if (searchData.value?.location) {
      // Si es la primera carga (no append), mostrar loading principal
      if (!append) {
        isLoadingResults.value = true;
        currentSkip.value = 0;
      } else {
        isLoadingMore.value = true;
      }
      
      // Obtener OrderBy del sort seleccionado si no se proporciona
      if (orderBy === undefined) {
        const currentSortOption = sortOptions.find(opt => opt.value === selectedSort.value);
        orderBy = currentSortOption?.orderBy || "Distancia"; // Por defecto: Distancia
      }
      
      // Si cambió el orden, resetear skip
      if (!append) {
        skip = 0;
        currentSkip.value = 0;
      }
      
      // Buscar cargadores con filtro por ubicación y ordenación
      const now = new Date();
      
      // Validar parámetros requeridos
      if (!searchData.value.location?.lat || !searchData.value.location?.lon) {
        throw new Error('La ubicación es requerida para buscar cargadores');
      }
      
      if (!searchData.value.IdCocheElectrico) {
        throw new Error('El vehículo es requerido para buscar cargadores');
      }
      
      // Construir queryParams filtrando valores null/undefined
      // Nota: Los nombres de parámetros deben coincidir exactamente con el swagger (PascalCase)
      const queryParams: Record<string, any> = {
        Latitud: searchData.value.location.lat,
        Longitud: searchData.value.location.lon,
        Take: TAKE_SIZE,
        Skip: skip,
        OrderBy: orderBy,
        BateriaInicial: searchData.value.BateriaInicial ?? searchData.value.batteryCurrent,
        BateriaFinal: searchData.value.BateriaFinal ?? searchData.value.batteryTarget,
        IdCocheElectrico: searchData.value.IdCocheElectrico,
        HoraCliente: formatHoraCliente(now),
      };
      
      // Solo agregar IdTarifaNombre si tiene un valor válido (no null ni undefined)
      if (searchData.value.IdTarifaNombre != null) {
        queryParams.IdTarifaNombre = searchData.value.IdTarifaNombre;
      }
      
      // Aplicar filtros si están activos
      if (activeFilters.value) {
        // Precio máximo (según swagger: PrecioHasta)
        if (activeFilters.value.priceMax > 0) {
          queryParams.PrecioHasta = activeFilters.value.priceMax;
        }
        
        // Estado del cargador: SoloLibres = true si está seleccionado "Libre"
        // Si selecciona "Todos" no hacemos nada (no pasamos el parámetro)
        if (activeFilters.value.chargerStatus === 'libre') {
          queryParams.SoloLibres = true;
        }
        
        // Tiempo de carga máximo (según swagger: TiempoMaximo, en minutos)
        if (activeFilters.value.chargingTime !== 'any') {
          const timeMap: Record<string, number> = {
            '<15': 15,
            '<30': 30,
            '<45': 45,
            '<60': 60
          };
          const maxTimeMinutes = timeMap[activeFilters.value.chargingTime];
          if (maxTimeMinutes) {
            queryParams.TiempoMaximo = maxTimeMinutes;
          }
        }
        
        // Distancia máxima (según swagger: RadioMetros, en metros)
        // Convertimos de km (valor del filtro) a metros
        if (activeFilters.value.distance > 0) {
          queryParams.RadioMetros = activeFilters.value.distance * 1000; // km a metros
        }
      }
      
      // Por defecto, si no hay filtro de distancia, usar 10000 metros (10 km)
      if (!queryParams.RadioMetros) {
        queryParams.RadioMetros = 10000;
      }
      
      // Filtrar parámetros null/undefined antes de enviar
      const cleanParams = Object.fromEntries(
        Object.entries(queryParams).filter(([_, value]) => value != null && value !== undefined && value !== '')
      );
      
      console.log('📤 Parámetros de búsqueda:', cleanParams);
      
      const cargadores = await api.callStandardApi(
        "getCargadorAll",
        { showLoading: false },
        queryParams["Take"],
        queryParams["Skip"],
        queryParams["BateriaInicial"],
        queryParams["BateriaFinal"],
        queryParams["IdCocheElectrico"],
        queryParams["IdTarifaNombre"],
        queryParams["Latitud"],
        queryParams["Longitud"],
        queryParams["OrderBy"],
        queryParams["SoloLibres"],
        queryParams["HoraCliente"],
        queryParams["PrecioHasta"],
        queryParams["RadioMetros"],
        queryParams["TiempoMaximo"]
      );
      
      console.log('✅ Cargadores obtenidos:', cargadores);
      
      // Transformar datos de la API al formato esperado
      let nuevosCargadores = cargadores
        .filter((item: any) => item.Latitud != null && item.Longitud != null) // Solo cargadores con coordenadas
        .map((item: any) => {
          return {
            id: item.Id,
            name: item.Direccion,
            distanceKm: item.DistanciaM ? Math.round(item.DistanciaM / 1000 * 10) / 10 : 0, // convertir metros a km
            priceKwh: item.PrecioKwh || 0,
            totalCost: item.CosteTotal || 0,
            powerKw: item.Potencia || 0,
            chargeTimeMin: item.TiempoCarga || calculateChargeTime(item.Potencia, searchData.value?.batteryCurrent || 0, searchData.value?.batteryTarget || 0),
            latitud: item.Latitud,
            longitud: item.Longitud,
            libre: item.Libre ?? 0,
            total: item.Total ?? 0
          };
        });
      
      // Aplicar filtros del lado del cliente (para filtros que no soporta la API o como respaldo)
      if (activeFilters.value) {
        nuevosCargadores = nuevosCargadores.filter((item: ChargerItem) => {
          // Filtro por precio máximo
          if (activeFilters.value!.priceMax > 0 && item.priceKwh > activeFilters.value!.priceMax) {
            return false;
          }
          
          // Filtro por distancia (ya debería venir filtrado del servidor, pero lo verificamos)
          if (activeFilters.value!.distance > 0 && item.distanceKm > activeFilters.value!.distance) {
            return false;
          }
          
          // Filtro por tiempo de carga
          if (activeFilters.value!.chargingTime !== 'any') {
            const timeMap: Record<string, number> = {
              '<15': 15,
              '<30': 30,
              '<45': 45,
              '<60': 60
            };
            const maxTimeMinutes = timeMap[activeFilters.value!.chargingTime];
            if (maxTimeMinutes && item.chargeTimeMin > maxTimeMinutes) {
              return false;
            }
          }
          
          // Filtro por estado del cargador
          if (activeFilters.value!.chargerStatus === 'libre' && item.libre === 0) {
            return false;
          }
          
          return true;
        });
      }
      
      // Si hay menos resultados de los solicitados, no hay más resultados
      if (nuevosCargadores.length < TAKE_SIZE) {
        hasMoreResults.value = false;
      }
      
      // Añadir o reemplazar resultados según si es append
      if (append) {
        results.value = [...results.value, ...nuevosCargadores];
        currentSkip.value += nuevosCargadores.length;
      } else {
        results.value = nuevosCargadores;
        currentSkip.value = nuevosCargadores.length;
        hasMoreResults.value = nuevosCargadores.length === TAKE_SIZE; // Si obtuvo todos los solicitados, puede haber más
      }
      
      console.log('📊 Resultados procesados:', results.value.length, 'total');
    } else {
      console.warn('⚠️ No hay ubicación disponible para buscar cargadores');
      apiError.value = t("components.chargerresultslistview.no_ubicacion_cargadores");
    }
    
  } catch (error: any) {
    console.error('❌ Error cargando resultados:', error);
    
    // Mensajes de error más específicos
    if (error?.response?.status === 400) {
      const errorMessage = error?.response?.data?.message || error?.response?.data || error?.message || '';
      apiError.value = t("components.chargerresultslistview.error_solicitud", { message: errorMessage });
      console.error('🔴 Detalles del error 400:', {
        url: error?.config?.url,
        params: error?.config?.params,
        response: error?.response?.data
      });
    } else if (error?.response?.status === 401) {
      apiError.value = t("components.chargerresultslistview.no_autorizado");
    } else if (error?.response?.status === 500) {
      apiError.value = t("components.chargerresultslistview.error_servidor");
    } else if (error?.message) {
      apiError.value = error.message;
    } else {
      apiError.value = t("components.chargerresultslistview.error_cargar_resultados");
    }
  } finally {
    isLoadingResults.value = false;
    isLoadingMore.value = false;
  }
}

// Manejar el scroll para cargar más resultados
function handleScroll(event: Event) {
  const target = event.target as HTMLElement;
  if (!target || isLoadingMore.value || !hasMoreResults.value) {
    return;
  }
  
  // Calcular si estamos cerca del final (últimos 200px)
  const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
  
  if (scrollBottom < 200) {
    loadMoreResults();
  }
}

// Cargar más resultados
async function loadMoreResults() {
  if (isLoadingMore.value || !hasMoreResults.value) {
    return;
  }
  
  await loadSearchResults(undefined, currentSkip.value, true);
}

// Calcular coste total basado en la tarifa y diferencia de batería
function calculateTotalCost(pricePerKwh: number, currentPercent: number, targetPercent: number): number {
  if (!pricePerKwh) return 0;
  
  // Asumir una batería de 50kWh promedio (esto debería venir del vehículo seleccionado)
  const batteryCapacity = 50;
  const energyNeeded = (targetPercent - currentPercent) / 100 * batteryCapacity;
  
  return Math.round(energyNeeded * pricePerKwh * 100) / 100;
}

// Calcular tiempo de carga basado en la potencia del cargador
function calculateChargeTime(powerKw: number, currentPercent: number, targetPercent: number): number {
  if (!powerKw) return 0;
  
  // Asumir una batería de 50kWh promedio
  const batteryCapacity = 50;
  const energyNeeded = (targetPercent - currentPercent) / 100 * batteryCapacity;
  
  // Tiempo en minutos
  return Math.round((energyNeeded / powerKw) * 60);
}

// Función para manejar el cambio de ordenación
async function handleSortChange(sortValue: typeof sortOptions[number]["value"]) {
  selectedSort.value = sortValue;
  const sortOption = sortOptions.find(opt => opt.value === sortValue);
  if (sortOption) {
    // Resetear variables de paginación al cambiar orden
    hasMoreResults.value = true;
    currentSkip.value = 0;
    await loadSearchResults(sortOption.orderBy, 0, false);
  }
}

// Watcher para recargar filtros cuando cambia la ruta (navegación entre vistas)
watch(() => route.path, async (newPath) => {
  if (newPath === '/charger-results') {
    // Recargar filtros desde localStorage al entrar en la vista de lista
    const previousFilters = activeFilters.value ? JSON.stringify(activeFilters.value) : null;
    loadFiltersFromStorage();
    const currentFilters = activeFilters.value ? JSON.stringify(activeFilters.value) : null;
    
    // Si los filtros cambiaron, recargar resultados
    if (previousFilters !== currentFilters) {
      hasMoreResults.value = true;
      currentSkip.value = 0;
      await loadSearchResults();
    }
  }
});

// Hook para cuando el componente se activa (si está dentro de keep-alive)
onActivated(async () => {
  // Recargar filtros y resultados cuando se activa el componente
  loadFiltersFromStorage();
  hasMoreResults.value = true;
  currentSkip.value = 0;
  await loadSearchResults();
});

onMounted(async () => {
  // Cargar filtros desde localStorage antes de cargar resultados
  loadFiltersFromStorage();
  
  // Cargar resultados inmediatamente
  await loadSearchResults();
  
  // Ocultar splash una vez que terminó la carga
  showSplash.value = false;
});

function openFilters() {
  showFilters.value = true;
}

// Cargar filtros desde localStorage
function loadFiltersFromStorage() {
  try {
    const storedFilters = localStorage.getItem('chargerActiveFilters');
    if (storedFilters) {
      activeFilters.value = JSON.parse(storedFilters);
      console.log('📥 Filtros cargados desde localStorage:', activeFilters.value);
    }
  } catch (error) {
    console.warn('⚠️ Error cargando filtros desde localStorage:', error);
  }
}

// Guardar filtros en localStorage
function saveFiltersToStorage(filters: ActiveFilters) {
  try {
    localStorage.setItem('chargerActiveFilters', JSON.stringify(filters));
    console.log('💾 Filtros guardados en localStorage:', filters);
  } catch (error) {
    console.warn('⚠️ Error guardando filtros en localStorage:', error);
  }
}

// Manejar la aplicación de filtros
function handleApplyFilters(filters: ActiveFilters) {
  activeFilters.value = filters;
  // Guardar filtros en localStorage para compartir entre vistas
  saveFiltersToStorage(filters);
  // Resetear paginación y recargar resultados con los nuevos filtros
  hasMoreResults.value = true;
  currentSkip.value = 0;
  loadSearchResults(undefined, 0, false);
}
</script>

<style scoped lang="scss">
@import "@/styles/_vars.scss";
@import "@/styles/figma.scss";
@import "@/styles/_themevars.scss";

.page {
  background: var(--background-default-default);
  min-height: 100dvh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
}

.splash {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100dvh;
  flex-direction: column;

  .texto-cargando {
    color: var(--text-default-caption, #667790);
    font-size: var(--font-size-body-sm, 14px);
    font-weight: 400;
    line-height: var(--line-height-body-sm, 18px);
  }

}

.layout {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100dvh;
  flex: 1;

  .header-container {
    flex: 0 0 auto;
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: var(--background-default-default);
    padding: var(--spacing-md, 16px) 0;

    .title-bar {
      padding-left: var(--spacing-lg, 24px);
      display: flex;
      align-items: center;
      justify-content: start;
      gap: var(--spacing-xs, 8px);
    }

    .title-bar-button {
      background: transparent;
      border: 0;
      font-size: 30px;
      padding: var(--spacing-xs, 8px) var(--spacing-md, 16px);
      width: auto;
      display: flex;

      i {
        color: var(--text-default-caption, #667790);
      }
    }

    h1 {
      color: var(--text-default-default, #000);
      font-size: var(--font-size-heading-h2, 24px);
      font-weight: 700;
      line-height: var(--line-height-heading-h2, 28px);
      margin: 0;
    }
  }

  main {
    overflow: auto;
    padding: var(--spacing-md, 16px) var(--spacing-lg, 24px) var(--spacing-lg, 24px);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-lg, 24px);
    flex: 1;
    min-height: 0;
  }

  :deep(.footer) {
    flex: 0 0 auto;
    margin-top: auto;
  }
}


.sort-bar {
  display: flex;
  gap: var(--spacing-xs, 8px);
  padding: var(--spacing-xs, 8px) 0;
  overflow-x: auto;
  max-width: 100vw;
  padding: 0 var(--spacing-lg, 24px) var(--spacing-xs, 8px);
}

.pill {
  padding: var(--tag-vertical-padding, 12px) var(--tag-horizontal-padding, 16px);
  border-radius: var(--tag-radius, 16px);
  border: 1px solid var(--input-border, #8390A2);
  white-space: nowrap;
  background: transparent;

  &.active {
    border: 1px solid var(--border-primary-default, #D3135A);
    background: var(--background-primary-default, #D3135A);
    color: var(--text-default-inverse, #FFF);
  }
}

.results {
  flex: 1;
  overflow-y: auto;
  padding: var(--spacing-md);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);

  .loading-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-xl);
    text-align: center;
    flex: 1;
    
    .loading-spinner {
      width: 40px;
      height: 40px;
      border: 4px solid var(--border-default-default, #C2CDDD);
      border-top: 4px solid var(--background-secondary-default, #1863ED);
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-bottom: var(--spacing-md);
    }
    
    p {
      color: var(--text-default-caption, #667790);
      font-size: var(--font-size-body-sm, 14px);
    }
  }

  .error-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-xl);
    text-align: center;
    background: var(--background-normal-hover, #F0F0F0);
    border-radius: var(--card-border-radius, 16px);
    flex: 1;
    
    .error-icon {
      font-size: 48px;
      margin-bottom: var(--spacing-md);
    }
    
    p {
      color: var(--text-error-default, #DC2626);
      font-size: var(--font-size-body-sm, 14px);
      margin-bottom: var(--spacing-md);
    }
    
    .retry-button {
      background: var(--background-secondary-default, #1863ED);
      color: white;
      border: none;
      padding: var(--spacing-sm) var(--spacing-md);
      border-radius: var(--border-radius-default, 8px);
      font-size: var(--font-size-body-sm, 14px);
      cursor: pointer;
      
      &:hover {
        opacity: 0.9;
      }
    }
  }

  .no-results {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-xl);
    text-align: center;
    flex: 1;
    
    .no-results-icon {
      font-size: 48px;
      margin-bottom: var(--spacing-md);
    }
    
    p {
      color: var(--text-default-caption, #667790);
      font-size: var(--font-size-body-sm, 14px);
      margin-bottom: var(--spacing-xs);
    }
    
    .no-results-subtitle {
      font-size: var(--font-size-body-xs, 12px);
      color: var(--text-default-caption, #667790);
      opacity: 0.8;
    }
  }
  
  .loading-more {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-lg);
    text-align: center;
    
    .loading-spinner {
      width: 32px;
      height: 32px;
      border: 3px solid var(--border-default-default, #C2CDDD);
      border-top: 3px solid var(--background-secondary-default, #1863ED);
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-bottom: var(--spacing-sm);
    }
    
    p {
      color: var(--text-default-caption, #667790);
      font-size: var(--font-size-body-sm, 14px);
    }
  }
  
  .no-more-results {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: var(--spacing-md);
    text-align: center;
    
    p {
      color: var(--text-default-caption, #667790);
      font-size: var(--font-size-body-sm, 14px);
      opacity: 0.8;
    }
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
